import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles,
  Copy,
  Save,
  History,
  Info,
  Shuffle,
  Download,
  DollarSign,
  Zap,
  Plus,
  Trash2,
  Edit2,
  Search,
  Filter,
  Tag,
  Star,
  Settings,
  ChevronDown,
  ChevronUp,
  Layers,
  Link,
  BarChart,
  Clock,
  TrendingUp,
  Package,
  Upload,
  Code,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Sliders,
  BookOpen,
  Target,
  Play,
  Bot
} from 'lucide-react';
import { promptTemplates, stylePresets } from '../data/templates';
import { multiModelTemplates, modelCapabilities } from '../data/multiModelTemplates';
import { PromptTemplate, GeneratedPrompt, PromptCategory, ModelPlatform } from '../types/prompt';
import PromptHistory from './PromptHistory';
import useLocalStorage from '../hooks/useLocalStorage';
import AIAssistant from './AIAssistant';
import { useApiKeys } from '../contexts/ApiKeyContext';
import aiService from '../services/aiService';
import { useAuth } from '../contexts/AuthContext';
import { promptsService, historyService } from '../services/firebase/firestore';
import toast from 'react-hot-toast';

interface CustomTemplate extends PromptTemplate {
  isCustom: boolean;
  author?: string;
  rating?: number;
  usageCount?: number;
  tags?: string[];
  version?: string;
}

interface PromptVariation {
  id: string;
  prompt: string;
  score?: number;
  notes?: string;
}

interface BatchOperation {
  id: string;
  name: string;
  variables: Record<string, string[]>;
  status: 'pending' | 'processing' | 'completed';
  results?: GeneratedPrompt[];
}

const PromptBuilderAdvanced: React.FC = () => {
  const { getApiKeyForModel } = useApiKeys();
  const { user, isAuthenticated, trackUsage } = useAuth();
  
  // State Management
  const [selectedTemplate, setSelectedTemplate] = useState<CustomTemplate | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [category, setCategory] = useState<PromptCategory | 'all'>('image');
  const [selectedPlatform, setSelectedPlatform] = useState<ModelPlatform | 'all'>('all');
  const [showAdvancedTemplates, setShowAdvancedTemplates] = useState(false);
  const [promptHistory, setPromptHistory] = useLocalStorage<GeneratedPrompt[]>('promptHistory', []);
  const [customTemplates, setCustomTemplates] = useLocalStorage<CustomTemplate[]>('customTemplates', []);
  const [favoriteTemplates, setFavoriteTemplates] = useLocalStorage<string[]>('favoriteTemplates', []);
  const [recentTemplates, setRecentTemplates] = useLocalStorage<string[]>('recentTemplates', []);
  const [promptVariations, setPromptVariations] = useState<PromptVariation[]>([]);
  const [batchOperations, setBatchOperations] = useState<BatchOperation[]>([]);
  
  // UI State
  const [showHistory, setShowHistory] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [showBatchMode, setShowBatchMode] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [showCostEstimate, setShowCostEstimate] = useState(false);
  const [showVariations, setShowVariations] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  
  // Advanced Features State
  const [negativePrompt, setNegativePrompt] = useState('');
  const [promptChain, setPromptChain] = useState<string[]>([]);
  const [variableDependencies, setVariableDependencies] = useState<Record<string, string[]>>({});
  const [tokenCount, setTokenCount] = useState(0);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [promptComplexity, setPromptComplexity] = useState<'simple' | 'moderate' | 'complex'>('simple');
  
  // Get all templates including custom ones
  const getAllTemplates = useCallback(() => {
    const standardTemplates = [...promptTemplates, ...multiModelTemplates].map(t => ({ ...t, isCustom: false }));
    return [...standardTemplates, ...customTemplates];
  }, [customTemplates]);

  // Filter templates based on search and filters
  const getFilteredTemplates = useCallback(() => {
    let templates = getAllTemplates();
    
    // Filter by category
    if (category !== 'all') {
      templates = templates.filter(t => t.category === category);
    }
    
    // Filter by platform
    if (selectedPlatform !== 'all') {
      templates = templates.filter(t => t.platform === selectedPlatform);
    }
    
    // Filter by search query
    if (searchQuery) {
      templates = templates.filter(t => 
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.template.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Filter by selected tags
    if (selectedTags.length > 0) {
      templates = templates.filter(t => 
        selectedTags.every(tag => t.tags?.includes(tag))
      );
    }
    
    // Filter by advanced templates
    if (!showAdvancedTemplates) {
      templates = templates.filter(t => !t.isAdvanced);
    }
    
    // Sort by favorites and recent usage
    templates.sort((a, b) => {
      const aFav = favoriteTemplates.includes(a.id) ? 1 : 0;
      const bFav = favoriteTemplates.includes(b.id) ? 1 : 0;
      if (aFav !== bFav) return bFav - aFav;
      
      const aRecent = recentTemplates.indexOf(a.id);
      const bRecent = recentTemplates.indexOf(b.id);
      if (aRecent !== -1 && bRecent !== -1) return aRecent - bRecent;
      if (aRecent !== -1) return -1;
      if (bRecent !== -1) return 1;
      
      return 0;
    });
    
    return templates;
  }, [category, selectedPlatform, searchQuery, selectedTags, showAdvancedTemplates, favoriteTemplates, recentTemplates, getAllTemplates]);

  // Handle template selection
  const handleTemplateSelect = (template: CustomTemplate) => {
    setSelectedTemplate(template);
    const initialVars: Record<string, string> = {};
    template.variables.forEach(v => {
      initialVars[v] = template.parameters?.[v] || '';
    });
    setVariables(initialVars);
    generatePrompt(template, initialVars);
    
    // Update recent templates
    setRecentTemplates(prev => {
      const filtered = prev.filter(id => id !== template.id);
      return [template.id, ...filtered].slice(0, 10);
    });
    
    // Update usage count for custom templates
    if (template.isCustom) {
      setCustomTemplates(prev => prev.map(t => 
        t.id === template.id 
          ? { ...t, usageCount: (t.usageCount || 0) + 1 }
          : t
      ));
    }
    
    // Auto-scroll to customize section with smooth animation
    setTimeout(() => {
      const customizeSection = document.querySelector('.customize-section-advanced');
      if (customizeSection) {
        customizeSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  // Enhance prompt with AI
  const enhanceWithAI = async () => {
    if (!generatedPrompt) {
      toast.error('Please generate a prompt first');
      return;
    }

    // Check for API keys
    const hasOpenAI = !!getApiKeyForModel('gpt4');
    const hasClaude = !!getApiKeyForModel('claude');
    const hasGemini = !!getApiKeyForModel('gemini');
    
    if (!hasOpenAI && !hasClaude && !hasGemini) {
      toast.error('Please configure at least one AI API key in your profile');
      return;
    }

    setIsEnhancing(true);
    
    try {
      // Try to use the best available model
      let response;
      let modelUsed = '';
      
      if (hasOpenAI) {
        response = await aiService.enhancePrompt(
          generatedPrompt,
          'gpt4',
          getApiKeyForModel('gpt4')
        );
        modelUsed = 'GPT-4';
      } else if (hasClaude) {
        response = await aiService.enhancePrompt(
          generatedPrompt,
          'claude',
          getApiKeyForModel('claude')
        );
        modelUsed = 'Claude';
      } else if (hasGemini) {
        response = await aiService.enhancePrompt(
          generatedPrompt,
          'gemini',
          getApiKeyForModel('gemini')
        );
        modelUsed = 'Gemini';
      }
      
      if (response?.success && response.data?.enhanced) {
        setGeneratedPrompt(response.data.enhanced);
        toast.success(`✨ Prompt enhanced with ${modelUsed}!`);
      } else {
        toast.error('Failed to enhance prompt. Please try again.');
      }
    } catch (error) {
      console.error('Enhancement error:', error);
      toast.error('An error occurred while enhancing the prompt');
    } finally {
      setIsEnhancing(false);
    }
  };

  // Generate prompt with advanced features
  const generatePrompt = (template: CustomTemplate, vars: Record<string, string>) => {
    let prompt = template.template;
    
    // Replace variables
    Object.entries(vars).forEach(([key, value]) => {
      prompt = prompt.replace(new RegExp(`{${key}}`, 'g'), value || `[${key}]`);
    });
    
    // Add negative prompt for image generation
    if (negativePrompt && (template.category === 'image' || template.category === 'video')) {
      if (template.platform === 'midjourney') {
        prompt += ` --no ${negativePrompt}`;
      } else if (template.platform === 'stable-diffusion-xl') {
        prompt += ` negative_prompt: ${negativePrompt}`;
      }
    }
    
    // Add to prompt chain if chaining is enabled
    if (promptChain.length > 0) {
      prompt = [...promptChain, prompt].join(' → ');
    }
    
    setGeneratedPrompt(prompt);
    updatePromptAnalytics(prompt);
  };

  // Update prompt analytics
  const updatePromptAnalytics = (prompt: string) => {
    // Calculate token count (approximate)
    const words = prompt.split(/\s+/).length;
    const tokens = Math.ceil(words * 1.3); // Rough estimation
    setTokenCount(tokens);
    
    // Calculate estimated cost
    if (selectedTemplate) {
      const model = selectedTemplate.platform;
      const capabilities = modelCapabilities[model];
      if (capabilities?.costPerToken) {
        setEstimatedCost(tokens * capabilities.costPerToken);
      }
    }
    
    // Determine complexity
    if (tokens < 50) {
      setPromptComplexity('simple');
    } else if (tokens < 150) {
      setPromptComplexity('moderate');
    } else {
      setPromptComplexity('complex');
    }
  };

  // Generate variations using AI
  const generateVariations = async () => {
    if (!generatedPrompt || !selectedTemplate) return;
    
    const apiKey = getApiKeyForModel(selectedTemplate.platform as any);
    if (!apiKey) {
      alert('Please configure API key for this model in settings');
      return;
    }
    
    setLoading(true);
    try {
      // Generate variations using AI
      const variations: PromptVariation[] = [];
      for (let i = 0; i < 3; i++) {
        const variation = await aiService.enhancePrompt(
          generatedPrompt,
          selectedTemplate.platform as any,
          apiKey
        );
        if (variation.success && variation.data) {
          variations.push({
            id: Date.now().toString() + i,
            prompt: variation.data.enhanced,
            score: Math.random() * 100,
            notes: `Variation ${i + 1}`
          });
        }
      }
      setPromptVariations(variations);
      setShowVariations(true);
    } catch (error) {
      console.error('Error generating variations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle batch operations
  const startBatchOperation = async () => {
    if (!selectedTemplate) return;
    
    const batchOp: BatchOperation = {
      id: Date.now().toString(),
      name: `Batch ${new Date().toLocaleString()}`,
      variables: {},
      status: 'processing',
      results: []
    };
    
    setBatchOperations(prev => [...prev, batchOp]);
    
    // Process batch
    // Implementation would go here
  };

  // Toggle favorite template
  const toggleFavorite = (templateId: string) => {
    setFavoriteTemplates(prev => 
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  // Save custom template
  const saveCustomTemplate = () => {
    if (!selectedTemplate || !generatedPrompt) return;
    
    const customTemplate: CustomTemplate = {
      ...selectedTemplate,
      id: 'custom_' + Date.now(),
      name: 'Custom: ' + selectedTemplate.name,
      template: generatedPrompt,
      isCustom: true,
      author: 'You',
      rating: 0,
      usageCount: 0,
      tags: [],
      version: '1.0.0'
    };
    
    setCustomTemplates(prev => [...prev, customTemplate]);
    setShowTemplateEditor(false);
  };

  // Export/Import templates
  const exportTemplates = () => {
    const data = {
      customTemplates,
      favoriteTemplates,
      promptHistory
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prompt_templates_${Date.now()}.json`;
    a.click();
  };

  const importTemplates = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        if (data.customTemplates) {
          setCustomTemplates(data.customTemplates);
        }
        if (data.favoriteTemplates) {
          setFavoriteTemplates(data.favoriteTemplates);
        }
        if (data.promptHistory) {
          setPromptHistory(data.promptHistory);
        }
      } catch (error) {
        console.error('Error importing templates:', error);
      }
    };
    reader.readAsText(file);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'c':
            if (generatedPrompt) {
              navigator.clipboard.writeText(generatedPrompt);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }
            break;
          case 's':
            e.preventDefault();
            if (generatedPrompt && selectedTemplate) {
              saveToHistory();
            }
            break;
          case 'k':
            e.preventDefault();
            setSearchQuery('');
            document.getElementById('template-search')?.focus();
            break;
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [generatedPrompt, selectedTemplate]);

  const saveToHistory = async () => {
    if (!generatedPrompt || !selectedTemplate) return;
    
    const newPrompt: GeneratedPrompt = {
      id: Date.now().toString(),
      prompt: generatedPrompt,
      platform: selectedTemplate.platform,
      timestamp: new Date(),
      isFavorite: false,
      category: selectedTemplate.category
    };
    
    setPromptHistory([newPrompt, ...promptHistory].slice(0, 100));
    
    // Save to Firestore if authenticated
    if (isAuthenticated && user) {
      const savePromise = (async () => {
        const promptData = {
          userId: user.uid,
          title: `Advanced: ${selectedTemplate.name} - ${new Date().toLocaleDateString()}`,
          content: generatedPrompt,
          platform: selectedTemplate.platform,
          category: selectedTemplate.category,
          tags: selectedTemplate.tags || [],
          parameters: variables,
          isPublic: false,
          isFavorite: false
        };
        
        const promptId = await promptsService.create(promptData);
        
        // Track usage
        await trackUsage('promptsSaved');
        
        // Add to history
        await historyService.add({
          userId: user.uid,
          action: 'saved',
          content: generatedPrompt,
          platform: selectedTemplate.platform
        });
        
        return promptId;
      })();
      
      toast.promise(
        savePromise,
        {
          loading: 'Saving advanced prompt...',
          success: '✅ Advanced prompt saved!',
          error: (err) => `Failed to save: ${err.message || err}`
        }
      );
      
      try {
        await savePromise;
      } catch (error) {
        console.error('Error saving to cloud:', error);
      }
    } else {
      toast.success('✅ Prompt saved to local history!');
    }
    
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white rounded-xl p-6 mb-6 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Sparkles className="w-10 h-10" />
            <div>
              <h1 className="text-3xl font-bold">Advanced Prompt Builder</h1>
              <p className="text-sm opacity-90 mt-1">Professional prompt engineering with AI assistance</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              title="Prompt History (Ctrl+H)"
            >
              <History className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowBatchMode(!showBatchMode)}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              title="Batch Mode"
            >
              <Package className="w-5 h-5" />
            </button>
            <button
              onClick={exportTemplates}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              title="Export Templates"
            >
              <Download className="w-5 h-5" />
            </button>
            <label className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors cursor-pointer"
              title="Import Templates">
              <Upload className="w-5 h-5" />
              <input
                type="file"
                accept=".json"
                onChange={importTemplates}
                className="hidden"
              />
            </label>
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          <div className="bg-white/10 rounded-lg p-2">
            <div className="text-xs opacity-75">Templates</div>
            <div className="text-lg font-bold">{getAllTemplates().length}</div>
          </div>
          <div className="bg-white/10 rounded-lg p-2">
            <div className="text-xs opacity-75">Custom</div>
            <div className="text-lg font-bold">{customTemplates.length}</div>
          </div>
          <div className="bg-white/10 rounded-lg p-2">
            <div className="text-xs opacity-75">History</div>
            <div className="text-lg font-bold">{promptHistory.length}</div>
          </div>
          <div className="bg-white/10 rounded-lg p-2">
            <div className="text-xs opacity-75">Favorites</div>
            <div className="text-lg font-bold">{favoriteTemplates.length}</div>
          </div>
        </div>
      </div>

      {/* Advanced Search and Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="template-search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates by name, content, or tags..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowAdvancedTemplates(!showAdvancedTemplates)}
            className={`px-4 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
              showAdvancedTemplates
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Sliders className="w-4 h-4" />
            <span>Advanced</span>
            {showAdvancedTemplates && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 backdrop-blur text-xs rounded-full">ON</span>
            )}
          </button>
          <button
            onClick={() => setShowTemplateEditor(true)}
            className="px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all shadow-md flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create Template</span>
          </button>
        </div>

        {/* Category Tabs with Icons */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          <button
            onClick={() => setCategory('all')}
            className={`px-4 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              category === 'all'
                ? 'bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span>🎨</span> All
          </button>
          <button
            onClick={() => setCategory('image')}
            className={`px-4 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              category === 'image'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span>🖼️</span> Image
          </button>
          <button
            onClick={() => setCategory('video')}
            className={`px-4 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              category === 'video'
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span>🎬</span> Video
          </button>
          <button
            onClick={() => setCategory('text')}
            className={`px-4 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              category === 'text'
                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span>📝</span> Text
          </button>
          <button
            onClick={() => setCategory('code')}
            className={`px-4 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              category === 'code'
                ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span>💻</span> Code
          </button>
          <button
            onClick={() => setCategory('analysis')}
            className={`px-4 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              category === 'analysis'
                ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span>📊</span> Analysis
          </button>
          <button
            onClick={() => setCategory('creative')}
            className={`px-4 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              category === 'creative'
                ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span>✨</span> Creative
          </button>
        </div>

        {/* Platform Filter and Template Count */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Platform:</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value as ModelPlatform | 'all')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="all">All Platforms</option>
              <optgroup label="Image Generation">
                <option value="midjourney">Midjourney</option>
                <option value="google-imagefx">Google ImageFX</option>
                <option value="dalle3">DALL-E 3</option>
                <option value="stable-diffusion-xl">Stable Diffusion XL</option>
              </optgroup>
              <optgroup label="Video Generation">
                <option value="midjourney-video">Midjourney Video</option>
                <option value="kling-ai">Kling AI</option>
                <option value="veo">Google Veo</option>
              </optgroup>
              <optgroup label="AI Models">
                <option value="claude">Claude</option>
                <option value="gpt4">GPT-4</option>
                <option value="gpt4-vision">GPT-4 Vision</option>
                <option value="gemini">Gemini</option>
                <option value="llama">LLaMA</option>
                <option value="mistral">Mistral</option>
              </optgroup>
              <option value="universal">Universal</option>
            </select>
          </div>
          
          {/* Template Counter */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="font-medium">{getFilteredTemplates().length}</span>
            <span>templates found</span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="ml-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear search
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Template Grid with Enhanced Features */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {getFilteredTemplates().slice(0, 12).map(template => (
          <div
            key={template.id}
            className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all hover:shadow-xl group ${
              selectedTemplate?.id === template.id
                ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-purple-50 shadow-lg'
                : 'border-gray-200 hover:border-gray-400 bg-white'
            }`}
            onClick={() => handleTemplateSelect(template as CustomTemplate)}
          >
            {/* Favorite Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(template.id);
              }}
              className="absolute top-2 right-2 p-1"
            >
              <Star
                className={`w-4 h-4 ${
                  favoriteTemplates.includes(template.id)
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            </button>

            {/* Template Info */}
            <div className="mb-2">
              <h3 className="font-semibold text-gray-900 pr-8">{template.name}</h3>
              {template.isCustom && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  Custom
                </span>
              )}
            </div>
            
            <p className="text-xs text-gray-600 mb-3">{template.description}</p>
            
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  {template.platform}
                </span>
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  {template.category}
                </span>
              </div>
              {recentTemplates.includes(template.id) && (
                <Clock className="w-3 h-3 text-blue-500" />
              )}
            </div>
            
            {/* Usage stats for custom templates */}
            {template.isCustom && template.usageCount && (
              <div className="mt-2 text-xs text-gray-500">
                Used {template.usageCount} times
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Main Builder Section */}
      {selectedTemplate && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 customize-section-advanced">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Customize Your Prompt</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Toggle Preview"
              >
                {showPreview ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setShowCostEstimate(!showCostEstimate)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Cost Estimate"
              >
                <DollarSign className="w-5 h-5" />
              </button>
              <button
                onClick={generateVariations}
                disabled={loading}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Generate Variations"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Variable Inputs with Dependencies */}
          <div className="space-y-4 mb-6">
            {selectedTemplate.variables.map(variable => (
              <div key={variable} className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 capitalize">
                    {variable.replace(/_/g, ' ')}
                  </label>
                  {stylePresets[variable as keyof typeof stylePresets] && (
                    <button
                      onClick={() => {
                        const presets = stylePresets[variable as keyof typeof stylePresets];
                        const randomValue = presets[Math.floor(Math.random() * presets.length)];
                        setVariables(prev => ({ ...prev, [variable]: randomValue }));
                        if (selectedTemplate) {
                          generatePrompt(selectedTemplate, { ...variables, [variable]: randomValue });
                        }
                      }}
                      className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                      title="Randomize"
                    >
                      <Shuffle className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={variables[variable] || ''}
                  onChange={(e) => {
                    const newVars = { ...variables, [variable]: e.target.value };
                    setVariables(newVars);
                    if (selectedTemplate) {
                      generatePrompt(selectedTemplate, newVars);
                    }
                  }}
                  placeholder={`Enter ${variable.replace(/_/g, ' ')}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {stylePresets[variable as keyof typeof stylePresets] && (
                  <div className="flex flex-wrap gap-1.5">
                    {stylePresets[variable as keyof typeof stylePresets].slice(0, 8).map(preset => (
                      <button
                        key={preset}
                        onClick={() => {
                          const newVars = { ...variables, [variable]: preset };
                          setVariables(newVars);
                          if (selectedTemplate) {
                            generatePrompt(selectedTemplate, newVars);
                          }
                        }}
                        className="text-xs px-2 py-1 bg-gray-50 hover:bg-blue-50 hover:text-blue-700 rounded-full transition-all border border-gray-200 hover:border-blue-300"
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Negative Prompt for Image Generation */}
          {(selectedTemplate.category === 'image' || selectedTemplate.category === 'video') && (
            <div className="mb-6">
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Negative Prompt (what to avoid)
              </label>
              <input
                type="text"
                value={negativePrompt}
                onChange={(e) => {
                  setNegativePrompt(e.target.value);
                  if (selectedTemplate) {
                    generatePrompt(selectedTemplate, variables);
                  }
                }}
                placeholder="ugly, blurry, low quality, distorted..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Preview Section with Analytics */}
          {showPreview && (
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Generated Prompt</h3>
                <div className="flex items-center gap-4">
                  {/* Analytics Badges */}
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    {tokenCount} tokens
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    promptComplexity === 'simple' ? 'bg-green-100 text-green-700' :
                    promptComplexity === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {promptComplexity}
                  </span>
                  {showCostEstimate && estimatedCost > 0 && (
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                      ~${estimatedCost.toFixed(4)}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
                <div className="relative p-4 bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 rounded-xl border border-gray-200">
                  <pre className="text-gray-800 font-mono text-sm whitespace-pre-wrap">
                    {generatedPrompt || 'Your AI prompt will appear here...'}
                  </pre>
                </div>
              </div>

              {/* Platform Info Message */}
              {selectedTemplate && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-700 flex items-start gap-1">
                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>
                      {selectedTemplate.platform === 'midjourney' && 'This prompt is optimized for Midjourney v7. Copy and paste directly into Discord.'}
                      {selectedTemplate.platform === 'midjourney-video' && 'This prompt is optimized for Midjourney Video. Use the --video flag in Discord.'}
                      {selectedTemplate.platform === 'kling-ai' && 'This prompt is optimized for Kling AI video generation.'}
                      {selectedTemplate.platform === 'google-imagefx' && 'This prompt is optimized for Google ImageFX.'}
                      {selectedTemplate.platform === 'dalle3' && 'This prompt is optimized for DALL-E 3.'}
                      {selectedTemplate.platform === 'stable-diffusion-xl' && 'This prompt is optimized for Stable Diffusion XL.'}
                      {selectedTemplate.platform === 'veo' && 'This prompt is optimized for Google Veo video generation.'}
                      {selectedTemplate.platform === 'claude' && 'This prompt is optimized for Claude AI.'}
                      {selectedTemplate.platform === 'gpt4' && 'This prompt is optimized for GPT-4.'}
                      {selectedTemplate.platform === 'gpt4-vision' && 'This prompt is optimized for GPT-4 Vision.'}
                      {selectedTemplate.platform === 'gemini' && 'This prompt is optimized for Google Gemini.'}
                      {selectedTemplate.platform === 'llama' && 'This prompt is optimized for LLaMA.'}
                      {selectedTemplate.platform === 'mistral' && 'This prompt is optimized for Mistral AI.'}
                      {selectedTemplate.platform === 'universal' && 'This is a universal prompt that works across multiple platforms.'}
                      {!['midjourney', 'midjourney-video', 'kling-ai', 'google-imagefx', 'dalle3', 'stable-diffusion-xl', 'veo', 'claude', 'gpt4', 'gpt4-vision', 'gemini', 'llama', 'mistral', 'universal'].includes(selectedTemplate.platform) && 
                        `This prompt is optimized for ${selectedTemplate.platform.charAt(0).toUpperCase() + selectedTemplate.platform.slice(1).replace(/-/g, ' ')}.`}
                    </span>
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={enhanceWithAI}
                  disabled={isEnhancing || !generatedPrompt}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    isEnhancing
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-sm'
                  }`}
                  title="Enhance prompt with AI"
                >
                  <Zap className="w-4 h-4" />
                  {isEnhancing ? 'Enhancing...' : 'Enhance with AI'}
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedPrompt);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    copied
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  <Copy className="w-4 h-4 inline mr-2" />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <button
                  onClick={saveToHistory}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    saved
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  <Save className="w-4 h-4 inline mr-2" />
                  {saved ? 'Saved!' : 'Save'}
                </button>
                <button
                  onClick={saveCustomTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4 inline mr-2" />
                  Save as Template
                </button>
              </div>
            </div>
          )}

          {/* Variations Section */}
          {showVariations && promptVariations.length > 0 && (
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">AI-Generated Variations</h3>
              <div className="space-y-3">
                {promptVariations.map(variation => (
                  <div key={variation.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">{variation.notes}</span>
                        {variation.score && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            Score: {variation.score.toFixed(0)}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(variation.prompt);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className="p-1 text-gray-500 hover:text-blue-600"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 font-mono">{variation.prompt}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Batch Mode Section */}
      {showBatchMode && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Batch Operations</h3>
            <button
              onClick={startBatchOperation}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Play className="w-4 h-4 inline mr-2" />
              Start Batch
            </button>
          </div>
          
          <div className="space-y-3">
            {batchOperations.map(op => (
              <div key={op.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{op.name}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    op.status === 'completed' ? 'bg-green-100 text-green-700' :
                    op.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {op.status}
                  </span>
                </div>
                {op.results && op.results.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    Generated {op.results.length} prompts
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Help */}
      <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-600">
        <h4 className="font-semibold mb-2">Keyboard Shortcuts:</h4>
        <div className="grid grid-cols-3 gap-2">
          <div><kbd>Ctrl+K</kbd> Search templates</div>
          <div><kbd>Ctrl+C</kbd> Copy prompt</div>
          <div><kbd>Ctrl+S</kbd> Save to history</div>
        </div>
      </div>

      {/* History Modal */}
      {showHistory && (
        <PromptHistory
          history={promptHistory}
          onClose={() => setShowHistory(false)}
          onSelect={(prompt) => {
            setGeneratedPrompt(prompt.prompt);
            setShowHistory(false);
          }}
          onToggleFavorite={(id) => {
            setPromptHistory(prev => prev.map(p =>
              p.id === id ? { ...p, isFavorite: !p.isFavorite } : p
            ));
          }}
          onDelete={(id) => {
            setPromptHistory(prev => prev.filter(p => p.id !== id));
          }}
          onCopy={(prompt) => {
            navigator.clipboard.writeText(prompt);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
        />
      )}

      {/* AI Assistant */}
      <AIAssistant
        currentPrompt={generatedPrompt}
        platform={selectedTemplate?.platform || 'universal'}
        onPromptUpdate={(prompt) => setGeneratedPrompt(prompt)}
      />
    </div>
  );
};

export default PromptBuilderAdvanced;